import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { X, ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { fetchUser } from "../../redux/actions/fetchUser";

import BaseSuite from "./graphs/baseSuite/baseSuite";
import LprInfringementSuite from "./graphs/lprInfringement/InfringementSuite";
import LprBaseSuite from "./graphs/baseLpr/LprBaseSuite";
import Reid from "./graphs/reid/Reid";
import LprJourney from "./graphs/lprJourney/LprJourney";
import { useLocation, useNavigate } from "react-router-dom";
import "../../styles/analytics.css";
import { url } from "../../api/endpointURL";
import GraphSuite from "../GraphSuite";
import QuantumScatterLoader from "./QuantumScatterLoader";
import SearchableSelect from "../SearchableSelect";

import { updateUserActivity } from "../access/accessControlUtils";
import { fetchRBACWithCache } from "../../utils/rbacCache";
import lprJourneyAPI from "../../services/lprJourneyAPI";

const PROJECT_START_YEAR = 2022;

const Analytics = () => {
  // Get current year - if after June, use next year, otherwise current year
  const getCurrentYear = () => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear();
  };

  const getRealYear = () => {
    const now = new Date();
    return now.getFullYear();
  };

  let currentYear = getCurrentYear();
  const years = Array.from(
    { length: currentYear - PROJECT_START_YEAR },
    (_, i) => currentYear - i
  );

  // Existing state from your component
  const [selectedBusiness, setSelectedBusiness] = useState(
    () => localStorage.getItem("analytics_selected_business") || ""
  );
  const [selectedSite, setSelectedSite] = useState(() =>
    JSON.parse(localStorage.getItem("analytics_selected_site") || "null")
  );
  const [selectedYear, setSelectedYear] = useState(
    () =>
      parseInt(localStorage.getItem("analytics_selected_year")) || getRealYear()
  );
  const [selectedCamera, setSelectedCamera] = useState(
    () => localStorage.getItem("analytics_selected_camera") || "All"
  );
  const [isSiteSelectedByUser, setIsSiteSelectedByUser] = useState(
    () => !!localStorage.getItem("analytics_selected_site")
  );
  const [cameraData, setCameraData] = useState([]);
  const [loadingSelectedCamera, setLoadingSelectedCamera] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState("baseSuite");
  const [cameraDataCache, setCameraDataCache] = useState({});
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const [fetchUserData, setFetchUserData] = useState(null);

  // New state for technician sites
  const [technicianSites, setTechnicianSites] = useState([]);
  const [loadingTechnicianSites, setLoadingTechnicianSites] = useState(false);
  const [showQuantumLoader, setShowQuantumLoader] = useState(true);

  // LPR Journey: network selection (replaces site filter for lprJourney suite)
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [loadingNetworks, setLoadingNetworks] = useState(false);

  // CONSOLIDATED RBAC STATE - Single source of truth
  const [rbacData, setRbacData] = useState({
    globalRole: "",
    businessRoles: [],
    isLoaded: false,
  });

  const hasTechnicianRoleForAnyBusiness = (businessRoles) => {
    return businessRoles.some(
      (business) => business.role?.toLowerCase() === "technician"
    );
  };

  // Use centralized RBAC caching service
  const fetchAndStoreRBAC = async (email) => {
    try {
      const rbacResponse = await fetchRBACWithCache(email);

      // Update state - single source of truth
      setRbacData({
        globalRole: rbacResponse.global_role,
        businessRoles: rbacResponse.business_roles || [],
        isLoaded: true,
      });

      return rbacResponse;
    } catch (error) {
      console.error("Error fetching RBAC data:", error);
      // Fallback to localStorage if fetch fails
      const storedRole = localStorage.getItem("rbac_role") || "";
      let storedBusinessRoles = [];
      try {
        storedBusinessRoles =
          JSON.parse(localStorage.getItem("rbac_data")) || [];
      } catch {
        storedBusinessRoles = [];
      }

      setRbacData({
        globalRole: storedRole,
        businessRoles: storedBusinessRoles,
        isLoaded: true,
      });
    }
  };
  useEffect(() => {
    if (selectedBusiness) {
      localStorage.setItem("analytics_selected_business", selectedBusiness);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (selectedSite) {
      localStorage.setItem(
        "analytics_selected_site",
        JSON.stringify(selectedSite)
      );
    }
  }, [selectedSite]);

  useEffect(() => {
    localStorage.setItem("analytics_selected_year", selectedYear.toString());
  }, [selectedYear]);

  useEffect(() => {
    localStorage.setItem("analytics_selected_camera", selectedCamera);
  }, [selectedCamera]);

  // SINGLE useEffect for initialization - prevent multiple fetches
  useEffect(() => {
    const email = localStorage.getItem("email");

    if (!email) {
      window.location.href = "/#auth/signin";
      return;
    }

    const initializeData = async () => {
      // Fetch RBAC data first
      const rbacResponse = await fetchAndStoreRBAC(email);
      const globalRole = rbacResponse?.global_role || rbacData.globalRole;
      const businessRoles =
        rbacResponse?.business_roles || rbacData.businessRoles;

      // console.log(
      //   "Analytics initialization - Email:",
      //   email,
      //   "Role:",
      //   globalRole
      // );
      // console.log("Business roles:", businessRoles);

      // Fetch business data based on role
      if (globalRole === "tla") {
        fetchAllBusinessesWithSites(setFetchUserData);
      } else {
        dispatch(fetchUser(email));
      }

      // Fetch technician sites if needed
      const isGlobalTechnician = globalRole?.toLowerCase() === "technician";
      const hasBusinessTechnicianRole =
        hasTechnicianRoleForAnyBusiness(businessRoles);

      if (isGlobalTechnician || hasBusinessTechnicianRole) {
        // console.log("User has technician access, fetching technician sites...");
        fetchTechnicianSites(email).catch((err) => {
          console.warn(
            "Technician sites fetch failed, falling back to legacy system:",
            err
          );
        });
      }
    };

    initializeData();
  }, []); // Empty dependency array - run once only

  useEffect(() => {
    const timer = setTimeout(() => setShowQuantumLoader(false), 4500);
    return () => clearTimeout(timer);
  }, []);

  // Check if user is a technician - use rbacData state
  const isTechnician = useMemo(() => {
    const globalRole = rbacData.globalRole?.toLowerCase() === "technician";
    const hasBusinessTechRole = hasTechnicianRoleForAnyBusiness(
      rbacData.businessRoles
    );
    return globalRole || hasBusinessTechRole;
  }, [rbacData.globalRole, rbacData.businessRoles]);

  // New state for filter toggles
  const [enabledFilters, setEnabledFilters] = useState({
    business: false,
    site: true,
    year: false,
    camera: false,
    network: false,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Memoize suite component map to prevent unnecessary re-renders
  const suiteComponentMap = useMemo(() => ({
    baseSuite: BaseSuite,
    lprBaseSuite: LprBaseSuite,
    lprInfringement: LprInfringementSuite,
    reid: Reid,
    lprJourney: LprJourney,
  }), []);

  // fetch user response - FIX: Provide default empty object
  const reduxUserResponse = useSelector(
    (state) => state.fetchUserResponse || {}
  );
  const fetchUserResponse = fetchUserData || reduxUserResponse;

  // Process camera data to add "All" option
  const processedCameraData = useMemo(() => {
    if (!cameraData || !cameraData.length) return [];

    const hasAllOption = cameraData.some(
      (camera) => camera.camera_name === "All"
    );

    if (hasAllOption) {
      return [...cameraData];
    } else {
      return [{ camera_name: "All", camera_id: "All" }, ...cameraData];
    }
  }, [cameraData]);

  const businesses = fetchUserResponse?.businesses;

  const extractAndSortBySite = useCallback((filteredSites) => {
    return filteredSites
      .flatMap((item) => {
        const key = Object.keys(item)[0];
        const siteDataArray = item[key];
        return siteDataArray.map((siteData) => ({
          site: siteData.site,
          hwid: siteData.hwid,
        }));
      })
      .sort((a, b) => a.site.localeCompare(b.site));
  }, []);

  // New function to fetch technician sites
  const fetchTechnicianSites = async (email, year = selectedYear) => {
    try {
      setLoadingTechnicianSites(true);
      // Add year parameter to technician sites API call
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL || ""
        }/technicianSites?email=${encodeURIComponent(email)}&year=${year}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          // console.log("No technician sites found for user");
          setTechnicianSites([]);
          return [];
        }
        throw new Error(`Failed to fetch technician sites: ${response.status}`);
      }

      const sites = await response.json();
      // console.log("Fetched technician sites for year", year, ":", sites);
      setTechnicianSites(sites);
      return sites;
    } catch (error) {
      console.error("Error fetching technician sites:", error);
      setTechnicianSites([]);
      return [];
    } finally {
      setLoadingTechnicianSites(false);
    }
  };

  const fetchAllBusinessesWithSites = async (
    setFetchUserData,
    year = selectedYear
  ) => {
    try {
      setYearDataLoading(true);
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL || ""
        }/fetchAllBusinessesAndSites?year=${year}`
      );
      const result = await response.json();

      const formatted = {
        businesses: result,
      };

      setFetchUserData(formatted);
    } catch (error) {
      console.error("Error fetching all businesses for TLA:", error);
    } finally {
      setYearDataLoading(false);
    }
  };

  useEffect(() => {
    if (!rbacData.isLoaded) return;

    const email = localStorage.getItem("email");

    // Refetch business/site data when year changes
    if (rbacData.globalRole === "tla") {
      fetchAllBusinessesWithSites(setFetchUserData, selectedYear);
    } else {
      // For non-TLA users, you might need to modify fetchUser to accept year parameter
      dispatch(fetchUser(email, selectedYear));
    }

    // Also refetch technician sites if user is a technician
    const hasAnyTechnicianRole =
      rbacData.globalRole?.toLowerCase() === "technician" ||
      hasTechnicianRoleForAnyBusiness(rbacData.businessRoles);

    if (hasAnyTechnicianRole) {
      fetchTechnicianSites(email, selectedYear);
    }
  }, [
    selectedYear,
    rbacData.isLoaded,
    rbacData.globalRole,
    rbacData.businessRoles,
  ]);

  const getBusinessesByRolePriority = useCallback(
    (businessNames, businessRoles, globalRole) => {
      if (!businessNames || businessNames.length === 0) return [];

      const roleHierarchy = {
        admin: 1,
        supervisor: 2,
        technician: 3,
      };

      if (globalRole && globalRole.toLowerCase() === "tla") {
        return businessNames.sort();
      }

      const businessesByRole = {};

      businessNames.forEach((businessName) => {
        const businessRole = businessRoles.find(
          (br) => br.business === businessName
        );
        const role =
          businessRole?.role?.toLowerCase() ||
          globalRole?.toLowerCase() ||
          "technician";

        const priority = roleHierarchy[role] || 999;

        if (!businessesByRole[priority]) {
          businessesByRole[priority] = [];
        }
        businessesByRole[priority].push(businessName);
      });

      const sortedPriorities = Object.keys(businessesByRole)
        .map(Number)
        .sort((a, b) => a - b);

      const result = [];
      sortedPriorities.forEach((priority) => {
        businessesByRole[priority].sort();
        result.push(...businessesByRole[priority]);
      });

      return result;
    },
    []
  );

  // Memoized business names - using rbacData state instead of localStorage
  const businessNames = useMemo(() => {
    if (!businesses || !rbacData.isLoaded) return [];

    let names = businesses.map((business) => Object.keys(business)[0]) || [];
    names = names.filter((name) => name && name.length > 0);

    if (rbacData.globalRole?.toLowerCase() === "technician") {
      const technicianBusinesses = [
        ...new Set(technicianSites.map((site) => site.business)),
      ];
      names = names.filter((name) => technicianBusinesses.includes(name));
    } else if (rbacData.globalRole?.toLowerCase() !== "tla") {
      const allowedBusinesses = rbacData.businessRoles.map((b) => b.business);
      names = names.filter((name) => allowedBusinesses.includes(name));

      const businessesWithTechnicianRole = rbacData.businessRoles
        .filter((br) => br.role?.toLowerCase() === "technician")
        .map((br) => br.business);

      if (
        businessesWithTechnicianRole.length > 0 &&
        technicianSites.length > 0
      ) {
        const technicianBusinesses = [
          ...new Set(technicianSites.map((site) => site.business)),
        ];
        names = names.filter((name) => {
          if (businessesWithTechnicianRole.includes(name)) {
            return technicianBusinesses.includes(name);
          }
          return true;
        });
      }
    }

    return getBusinessesByRolePriority(
      names,
      rbacData.businessRoles,
      rbacData.globalRole
    );
  }, [businesses, rbacData, technicianSites, getBusinessesByRolePriority]);

  // Updated sortedSites with proper dependencies
  const sortedSites = useMemo(() => {
    if (!selectedBusiness || !businesses || !rbacData.isLoaded) return [];

    const filtered = businesses.filter(
      (obj) => obj[selectedBusiness] !== undefined
    );
    if (!filtered || filtered.length === 0) return [];

    let extracted = extractAndSortBySite(filtered);

    const userRoleForBusiness = rbacData.businessRoles.find(
      (br) => br.business === selectedBusiness
    );
    const isBusinessTechnician =
      userRoleForBusiness?.role?.toLowerCase() === "technician";
    const isGlobalTechnician =
      rbacData.globalRole?.toLowerCase() === "technician";

    if (isGlobalTechnician || isBusinessTechnician) {
      if (technicianSites && technicianSites.length > 0) {
        const allowedSitesForBusiness = technicianSites
          .filter((ts) => ts.business === selectedBusiness)
          .map((ts) => ts.site);

        extracted = extracted.filter((site) =>
          allowedSitesForBusiness.includes(site.site)
        );
      } else {
        console.warn("No technician sites available for filtering");
        extracted = [];
      }
    } else if (
      rbacData.globalRole?.toLowerCase() !== "tla" &&
      selectedBusiness
    ) {
      if (
        userRoleForBusiness &&
        userRoleForBusiness.sites &&
        Array.isArray(userRoleForBusiness.sites)
      ) {
        extracted = extracted.filter((site) =>
          userRoleForBusiness.sites.includes(site.site)
        );
      }
    }

    return extracted;
  }, [
    selectedBusiness,
    businesses,
    rbacData,
    technicianSites,
    extractAndSortBySite,
  ]);

  // Auto-enable filters for technicians when they have sites
  useEffect(() => {
    if (!rbacData.isLoaded) return;

    const hasAnyTechnicianRole =
      rbacData.globalRole?.toLowerCase() === "technician" ||
      hasTechnicianRoleForAnyBusiness(rbacData.businessRoles);

    if (hasAnyTechnicianRole && technicianSites.length > 0) {
      const businessesFromSites = [
        ...new Set(technicianSites.map((site) => site.business)),
      ];

      setEnabledFilters((prev) => ({
        ...prev,
        business: businessesFromSites.length > 1,
        site: true,
      }));
    }
  }, [rbacData, technicianSites]);

  // Initialize default selections when business data is available
  useEffect(() => {
    if (businessNames.length > 0 && !selectedBusiness && rbacData.isLoaded) {
      const firstBusiness = businessNames[0];
      setSelectedBusiness(firstBusiness);
      // console.log("Auto-selecting highest priority business:", firstBusiness);
    }
  }, [businessNames.length, selectedBusiness, rbacData.isLoaded]);

  // Auto-select first site when business changes and sites are available
  useEffect(() => {
    if (selectedBusiness && sortedSites.length > 0 && !isSiteSelectedByUser) {
      const firstSite = sortedSites[0];
      setSelectedSite(firstSite);
      setIsSiteSelectedByUser(false);
      // console.log(
      //   "Auto-selecting first site for year",
      //   selectedYear,
      //   ":",
      //   firstSite
      // );

      fetchCameras(selectedBusiness, firstSite, selectedSuite);
    }
  }, [
    selectedBusiness,
    sortedSites.length,
    isSiteSelectedByUser,
    selectedSuite,
    selectedYear,
  ]); // selectedYear is already here

  // Fetch cameras when site is loaded from localStorage (persisted selection)
  useEffect(() => {
    if (
      selectedBusiness &&
      selectedSite &&
      isSiteSelectedByUser &&
      selectedSuite
    ) {
      // console.log("Fetching cameras for persisted site:", selectedSite);
      fetchCameras(selectedBusiness, selectedSite, selectedSuite);
    }
  }, [selectedBusiness, selectedSite?.hwid, selectedSuite]); // Use hwid to track site changes

  const [yearDataLoading, setYearDataLoading] = useState(false);

  const toggleFilter = (filterType) => {
    setEnabledFilters((prev) => {
      const isCurrentlyEnabled = prev[filterType];

      // If toggling off, just disable this filter
      if (isCurrentlyEnabled) {
        return {
          ...prev,
          [filterType]: false,
        };
      }

      // If toggling on, disable all others and enable only this one
      return {
        business: filterType === 'business',
        site: filterType === 'site',
        year: filterType === 'year',
        camera: filterType === 'camera',
        network: filterType === 'network',
      };
    });
  };

  // ─── LPR Journey: fetch networks when business changes ───────────────────
  const fetchNetworksForBusiness = useCallback(async (businessName) => {
    if (!businessName) {
      setNetworks([]);
      setSelectedNetwork(null);
      return;
    }
    console.log(`[fetchNetworksForBusiness] fetching networks for "${businessName}"`);
    setLoadingNetworks(true);
    try {
      const fetchedNetworks = await lprJourneyAPI.getNetworksByBusinessName(businessName);
      console.log(`[fetchNetworksForBusiness] received ${fetchedNetworks?.length} networks:`, fetchedNetworks);
      setNetworks(fetchedNetworks);
      // Auto-select first network
      if (fetchedNetworks.length > 0) {
        setSelectedNetwork(fetchedNetworks[0]);
      } else {
        setSelectedNetwork(null);
      }
    } catch (err) {
      console.error('[fetchNetworksForBusiness] Error fetching networks for LPR Journey:', err);
      setNetworks([]);
      setSelectedNetwork(null);
    } finally {
      setLoadingNetworks(false);
    }
  }, []);

  // Fetch networks when lprJourney suite is active and business changes
  useEffect(() => {
    console.log(`[lprJourney useEffect] selectedSuite=${selectedSuite}, selectedBusiness=${selectedBusiness}`);
    if (selectedSuite === 'lprJourney') {
      if (selectedBusiness) {
        console.log(`[lprJourney useEffect] calling fetchNetworksForBusiness("${selectedBusiness}")`);
        fetchNetworksForBusiness(selectedBusiness);
      } else {
        console.log(`[lprJourney useEffect] selectedBusiness is empty, skipping fetch`);
      }
    }
  }, [selectedSuite, selectedBusiness, fetchNetworksForBusiness]);

  const handleNetworkChange = (newNetwork) => {
    if (!newNetwork) return;
    setSelectedNetwork(newNetwork);
  };

  const handleBusinessChange = (newBusiness) => {
    if (!newBusiness) return;

    setSelectedBusiness(newBusiness);
    // Clear site when business changes and get sites for new business
    setSelectedSite(null);
    setSelectedCamera("All");
    setCameraData([]); // Clear camera data when business changes
    setIsSiteSelectedByUser(false); // Reset user selection flag

    // Get sites for the selected business and auto-select first one
    const sitesAndHwids = getSitesAndHwidsForBusiness(
      fetchUserResponse,
      newBusiness
    );
    if (sitesAndHwids && sitesAndHwids.length > 0) {
      setSelectedSite(sitesAndHwids[0]);
      // Fetch cameras for the first site automatically
      fetchCameras(newBusiness, sitesAndHwids[0], selectedSuite);
    }
  };

  const handleSiteChange = (newSite) => {
    if (!newSite) return;

    setIsSiteSelectedByUser(true);
    setSelectedSite(newSite);
    setSelectedCamera("All");
    // fetch camera data for the selected site
    fetchCameras(selectedBusiness, newSite, selectedSuite);
  };

  const handleYearChange = (newYear) => {
    if (!newYear) return;

    setSelectedYear(newYear);

    // Clear current selections to force refresh
    setSelectedSite(null);
    setSelectedCamera("All");
    setCameraData([]);
    setIsSiteSelectedByUser(false);

    // The useEffect above will trigger and refetch data for the new year
  };

  const handleCameraChange = (newCamera) => {
    if (!newCamera) return;
    setSelectedCamera(newCamera.camera_name);
  };

  // Handle suite change and update cameras
  const handleComponentChange = (component) => {
    setSelectedSuite(component);
    setSelectedCamera("All");
    if (selectedBusiness && selectedSite) {
      fetchCameras(selectedBusiness, selectedSite, component);
    }
  };

  // Memoized fetchCameras function (from your original code)
  const fetchCameras = useCallback(async (business, site, component) => {
    if (!business || !site || !component) {
      return;
    }

    // Create cache key from business, site hwid, and component
    const cacheKey = `${business}-${site.hwid}-${component}`;

    // Check if we have cached data for this combination
    if (cameraDataCache[cacheKey]) {
      // console.log('Using cached camera data for:', cacheKey);
      setCameraData(cameraDataCache[cacheKey]);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    try {
      setLoadingSelectedCamera(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/fetch_camera`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ business, site, analyticType: component }),
          signal,
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (currentRequestId === requestIdRef.current) {
        const cameraList = data.length ? data : [];
        setCameraData(cameraList);

        // Cache the camera data for future use
        setCameraDataCache(prev => ({
          ...prev,
          [cacheKey]: cameraList
        }));
        // console.log('Cached camera data for:', cacheKey);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error(`Error in fetchSelectedCamera: ${error}`);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingSelectedCamera(false);
      }
    }
  }, [cameraDataCache]);

  function getSitesAndHwidsForBusiness(data, targetBusiness) {
    const result = [];

    data?.businesses?.forEach((businessObj) => {
      if (businessObj.hasOwnProperty(targetBusiness)) {
        businessObj[targetBusiness].forEach((siteObj) => {
          result.push({
            site: siteObj.site,
            hwid: siteObj.hwid,
          });
        });
      }
    });

    return result;
  }

  // Login check and URL view parameter handling combined below

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get("view");

    if (
      viewParam &&
      ["baseSuite", "lprBaseSuite", "lprInfringement", "reid", "lprJourney"].includes(
        viewParam
      )
    ) {
      setSelectedSuite(viewParam);
    }
  }, [location.search]);

  // Debug logging (comment out in production)
  // // console.log("selectedSite:", selectedSite);
  // // console.log("Current selections:", {
  //   selectedBusiness,
  //   selectedSite: selectedSite?.site,
  //   selectedYear,
  //   businessNamesLength: businessNames.length,
  //   sortedSitesLength: sortedSites.length,
  //   technicianSitesLength: technicianSites.length,
  //   technicianSites: technicianSites,
  //   role: rbacData.globalRole,
  //   loadingTechnicianSites,
  // });

  return (
    <>
      {showQuantumLoader && <QuantumScatterLoader />}

      <div
        className={`analytics-container ${showQuantumLoader ? "blurred" : ""}`}
      >
        {/* Main Content */}
        <div className={`analytics-main-content`}>
          <div className="filter-content-wrapper">
            {/* Filter Toggle Buttons */}
            <div className="filter-toggle-section">
              <div className="filter-buttons-container">
                {businessNames && businessNames.length > 1 && (
                  <button
                    onClick={() => toggleFilter("business")}
                    className={`filter-toggle-btn ${
                      enabledFilters.business ? "filter-toggle-btn-active" : ""
                    }`}
                  >
                    Select Business {enabledFilters.business ? "✓" : ""}
                  </button>
                )}

                {selectedSuite !== 'lprJourney' && (
                  <button
                    onClick={() => toggleFilter("site")}
                    className={`filter-toggle-btn ${
                      enabledFilters.site ? "filter-toggle-btn-active" : ""
                    }`}
                    disabled={
                      loadingTechnicianSites &&
                      rbacData.globalRole?.toLowerCase() === "technician"
                    }
                  >
                    Select Site {enabledFilters.site ? "✓" : ""}{" "}
                    {loadingTechnicianSites &&
                    rbacData.globalRole?.toLowerCase() === "technician"
                      ? "(Loading...)"
                      : ""}
                  </button>
                )}

                {selectedSuite !== 'reid' && (
                  <button
                    onClick={() => toggleFilter("year")}
                    className={`filter-toggle-btn ${
                      enabledFilters.year ? "filter-toggle-btn-active" : ""
                    }`}
                  >
                    Select Year {enabledFilters.year ? "✓" : ""}
                  </button>
                )}

                {processedCameraData && processedCameraData.length > 1 && selectedSuite !== 'lprJourney' && selectedSuite !== 'reid' && (
                  <button
                    onClick={() => toggleFilter("camera")}
                    className={`filter-toggle-btn ${
                      enabledFilters.camera ? "filter-toggle-btn-active" : ""
                    }`}
                  >
                    Select Camera {enabledFilters.camera ? "✓" : ""}
                  </button>
                )}

                {/* Network filter — only shown for LPR Journey suite */}
                {selectedSuite === 'lprJourney' && (
                  <button
                    onClick={() => toggleFilter("network")}
                    className={`filter-toggle-btn ${
                      enabledFilters.network ? "filter-toggle-btn-active" : ""
                    }`}
                    disabled={loadingNetworks}
                  >
                    Select Network {enabledFilters.network ? "✓" : ""}{" "}
                    {loadingNetworks ? "(Loading...)" : ""}
                  </button>
                )}
              </div>
            </div>

            {/* Searchable Dropdowns for Enabled Filters */}
            <div className="filter-buttons-container">
              <div className="searchable-filters-container">
                {/* Business SearchableSelect */}
                {enabledFilters.business && businessNames && (
                  <div className="filter-select-wrapper">
                    <SearchableSelect
                      options={businessNames}
                      value={selectedBusiness}
                      onChange={handleBusinessChange}
                      placeholder="Search businesses..."
                      noOptionsText="No businesses available"
                    />
                  </div>
                )}

                {/* Site SearchableSelect - only show if business is selected and not lprJourney suite */}
                {enabledFilters.site && selectedBusiness && sortedSites && selectedSuite !== 'lprJourney' && (
                  <div className="filter-select-wrapper">
                    <SearchableSelect
                      options={sortedSites}
                      value={selectedSite}
                      onChange={handleSiteChange}
                      placeholder="Search sites..."
                      getOptionLabel={(option) => option?.site || ''}
                      getOptionKey={(option) => option?.hwid || option?.site}
                      isOptionEqualToValue={(option, value) => option?.hwid === value?.hwid}
                      noOptionsText="No sites available"
                      disabled={loadingTechnicianSites && rbacData.globalRole?.toLowerCase() === "technician"}
                    />
                  </div>
                )}

                {/* Year SearchableSelect — hidden on reid tab */}
                {enabledFilters.year && selectedSuite !== 'reid' && (
                  <div className="filter-select-wrapper">
                    <SearchableSelect
                      options={years}
                      value={selectedYear}
                      onChange={handleYearChange}
                      placeholder="Search years..."
                      getOptionLabel={(option) => option.toString()}
                      isOptionEqualToValue={(option, value) => option === value}
                      noOptionsText="No years available"
                    />
                  </div>
                )}

                {/* Camera SearchableSelect */}
                {enabledFilters.camera && processedCameraData && selectedSuite !== 'lprJourney' && selectedSuite !== 'reid' && (
                  <div className="filter-select-wrapper">
                    <SearchableSelect
                      options={processedCameraData}
                      value={processedCameraData.find(c => c.camera_name === selectedCamera) || null}
                      onChange={handleCameraChange}
                      placeholder="Search cameras..."
                      getOptionLabel={(option) => option?.camera_name || ''}
                      isOptionEqualToValue={(option, value) => option?.camera_id === value?.camera_id}
                      noOptionsText="No cameras available"
                    />
                  </div>
                )}

                {/* Network SearchableSelect — only for LPR Journey suite */}
                {enabledFilters.network && selectedSuite === 'lprJourney' && (
                  <div className="filter-select-wrapper">
                    <SearchableSelect
                      options={networks}
                      value={selectedNetwork}
                      onChange={handleNetworkChange}
                      placeholder="Search networks..."
                      getOptionLabel={(option) => option?.network_name || option?.network_id || ''}
                      getOptionKey={(option) => option?.network_id}
                      isOptionEqualToValue={(option, value) => option?.network_id === value?.network_id}
                      noOptionsText="No networks available"
                      disabled={loadingNetworks}
                    />
                  </div>
                )}
              </div>
              <p className="filter-help-text">
                {!selectedBusiness && enabledFilters.site
                  ? "Select a business first to see available sites"
                  : loadingTechnicianSites &&
                    rbacData.globalRole?.toLowerCase() === "technician"
                  ? "Loading your assigned sites..."
                  : rbacData.globalRole?.toLowerCase() === "technician" &&
                    technicianSites.length === 0
                  ? "No sites assigned to your account"
                  : ""}
              </p>
            </div>

            {/* Active Filters Display */}
            <div className="active-filters-display">
              {(selectedBusiness ||
                selectedSite ||
                selectedYear ||
                selectedCamera !== "All") && (
                <div className="active-filters-bar">
                  Selected:{" "}
                  {[
                    selectedBusiness && `Business(${selectedBusiness})`,
                    selectedSuite === 'lprJourney'
                      ? selectedNetwork && `Network(${selectedNetwork.network_name || selectedNetwork.network_id})`
                      : selectedSite?.site && `Site(${selectedSite.site})`,
                    selectedSuite !== 'reid' && `Year(${selectedYear})`,
                    selectedSuite !== 'lprJourney' && selectedSuite !== 'reid' && selectedCamera &&
                      selectedCamera !== "All" &&
                      `Camera(${selectedCamera})`,
                  ]
                    .filter(Boolean)
                    .join(" + ")}
                </div>
              )}
            </div>

            <div className="analytics-nav-container">
              <div className="analytics-nav-container-wrapper">
                <button
                  onClick={() => handleComponentChange("baseSuite")}
                  className={
                    selectedSuite === "baseSuite"
                      ? "analytics-nav-btn-selected"
                      : "analytics-nav-btn"
                  }
                >
                  BASE SUITE
                </button>
                <button
                  onClick={() => handleComponentChange("lprBaseSuite")}
                  className={
                    selectedSuite === "lprBaseSuite"
                      ? "analytics-nav-btn-selected"
                      : "analytics-nav-btn"
                  }
                >
                  LPR
                </button>
                <button
                  onClick={() => handleComponentChange("lprInfringement")}
                  className={
                    selectedSuite === "lprInfringement"
                      ? "analytics-nav-btn-selected"
                      : "analytics-nav-btn"
                  }
                >
                  LPR INFRINGEMENT
                </button>
                {/* REID Button */}
                <button
                  onClick={() => handleComponentChange("reid")}
                  className={
                    selectedSuite === "reid"
                      ? "analytics-nav-btn-selected"
                      : "analytics-nav-btn"
                  }
                >
                  REID
                </button>
                {/* LPR JOURNEY Button */}
                <button
                  onClick={() => handleComponentChange("lprJourney")}
                  className={
                    selectedSuite === "lprJourney"
                      ? "analytics-nav-btn-selected"
                      : "analytics-nav-btn"
                  }
                >
                  LPR JOURNEY
                </button>
                {!isTechnician && (
                  <>
                    <button
                      onClick={() => {
                        updateUserActivity(localStorage.getItem("email"));
                        navigate("/reporting");
                      }}
                      className="analytics-nav-btn"
                    >
                      REPORTING
                    </button>
                    <button
                      onClick={() => {
                        updateUserActivity(localStorage.getItem("email"));
                        navigate("/access");
                      }}
                      className="analytics-nav-btn"
                    >
                      ACCESS CONTROL
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Render the selected suite component */}
          {selectedSuite === 'lprJourney' ? (
            /* LPR Journey uses network-based selection, not site/camera */
            <LprJourney
              selectedNetwork={selectedNetwork}
              selectedBusiness={{ token: selectedBusiness }}
              selectedYear={selectedYear}
            />
          ) : (
            selectedSite && selectedCamera && (
              <div>
                <GraphSuite
                  SuiteComponent={suiteComponentMap[selectedSuite]}
                  selectedSite={selectedSite}
                  selectedCamera={selectedCamera}
                  selectedSuite={selectedSuite}
                  selectedYear={selectedYear}
                  processedCameraData={processedCameraData}
                />
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default Analytics;
